import Image from "next/image";
import type { ReactElement } from "react";
import { useCallback, useId, useState } from "react";

import { LOCAL_TAX_FAQ_URL } from "../../../../constants";
import { RequiredAnswerSection } from "../required-answer-section";

export type LocalTaxConsentTermsMessages = {
  title: string;
  body: string;
  linkText: string;
  agreeLabel: string;
  requiredLabel?: string;
};

const JA_MESSAGES: LocalTaxConsentTermsMessages = {
  title: "宿泊税・入湯税について",
  body: "各自治体の条例に基づいて、宿泊税・入湯税をチェックアウト時に別途請求させていただきます。ご同意の上でご予約ください。",
  linkText: "詳細はこちら",
  agreeLabel: "宿泊税・入湯税について同意する",
};

export function useLocalTaxConsentTerms(
  isRequired: boolean,
  messages: LocalTaxConsentTermsMessages = JA_MESSAGES,
): {
  renderLocalTaxConsentTerms: () => ReactElement | null;
  agreedOrUnnecessary: boolean;
} {
  const [agreed, setAgreed] = useState(false);

  const renderLocalTaxConsentTerms = useCallback(() => {
    if (!isRequired) {
      return null;
    }
    return (
      <LocalTaxConsentTerms
        agreed={agreed}
        setAgreed={setAgreed}
        messages={messages}
      />
    );
  }, [agreed, isRequired, messages]);

  return {
    renderLocalTaxConsentTerms,
    agreedOrUnnecessary: agreed || !isRequired,
  };
}

export function LocalTaxConsentTerms({
  agreed,
  setAgreed,
  messages = JA_MESSAGES,
}: {
  agreed: boolean;
  setAgreed: (value: boolean) => void;
  messages?: LocalTaxConsentTermsMessages;
}) {
  const checkboxId = useId();

  return (
    <RequiredAnswerSection
      title={messages.title}
      messages={
        messages.requiredLabel
          ? { requiredLabel: messages.requiredLabel }
          : undefined
      }
      actionArea={
        <label
          htmlFor={checkboxId}
          data-testid="local-tax-agree-checkbox"
          className="group flex cursor-pointer items-center gap-[9px]"
        >
          <input
            id={checkboxId}
            type="checkbox"
            className="hidden"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <Image
            className="hidden shrink-0 group-has-checked:block"
            src="/i/check-box-filled.svg"
            alt=""
            width={24}
            height={24}
          />
          <Image
            className="block shrink-0 group-has-checked:hidden"
            src="/i/check-box-outline-blank.svg"
            alt=""
            width={24}
            height={24}
          />
          <span className="text-skogecha-100 text-[14px] leading-[1.5] font-normal tracking-[0.15px] select-none">
            {messages.agreeLabel}
          </span>
        </label>
      }
    >
      <p>{messages.body}</p>
      <a
        data-analytics-id="reservation_form_clicked_local_tax_help"
        href={LOCAL_TAX_FAQ_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-[12px] inline-flex items-center gap-[5px] text-[12px] leading-[1.5] tracking-[0.15px]"
      >
        {messages.linkText}
        <Image src="/i/icon_question.svg" alt="" width={16} height={16} />
      </a>
    </RequiredAnswerSection>
  );
}
